<script>
  import { token } from "./store/token.js";
  import { section } from "./store/section.js";
  import { permissions, canAccess } from "./store/permissions.js";
  import { onMount } from "svelte";

  let currentSection = $section;
  let isMobileMenuOpen = false;

  $: {
    currentSection = $section;
  }

  const set_section = selected_section => {
    section.set(selected_section);
    isMobileMenuOpen = false;
  };

  const logout = () => {
    permissions.clear();
    token.set("");
  };

  // Close mobile menu when clicking outside
  onMount(() => {
    const handleClickOutside = (event) => {
      const navbar = document.querySelector('.navbar-container');
      if (navbar && !navbar.contains(event.target)) {
        isMobileMenuOpen = false;
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  });
</script>

<style>
  .navbar-container {
    background: var(--ds-surface);
    box-shadow: var(--ds-shadow-card);
    width: 280px;
    height: 100vh;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--ds-border);
  }

  .navbar-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 1.5rem 0;
  }

  .logo-container {
    padding: 0 1.5rem 1.5rem;
    border-bottom: 1px solid var(--ds-border);
    margin-bottom: 1.5rem;
  }

  .logo {
    height: 40px;
    transition: transform 0.2s ease;
  }

  .logo:hover {
    transform: scale(1.02);
  }

  .nav-links {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0 1rem;
    list-style: none;
    flex: 1;
  }

  .nav-item {
    position: relative;
  }

  .nav-link {
    color: var(--ds-text-muted);
    text-decoration: none;
    padding: 0.75rem 1rem;
    border-radius: var(--ds-radius-sm);
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
  }

  .nav-link:hover {
    color: var(--ds-text);
    background: var(--ds-surface-2);
  }

  .nav-link.active {
    color: var(--ds-accent);
    background: var(--ds-accent-soft);
  }

  .nav-link.active::before {
    content: '';
    position: absolute;
    left: -1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 24px;
    background: var(--ds-accent);
    border-radius: 0 2px 2px 0;
  }

  .nav-link svg {
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }

  .logout-section {
    margin-top: auto;
    padding: 1.5rem 1rem 0;
    border-top: 1px solid var(--ds-border);
  }

  .logout-button {
    color: var(--ds-danger);
    background: transparent;
    padding: 0.75rem 1rem;
    border-radius: var(--ds-radius-sm);
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
  }

  .logout-button:hover {
    background: var(--ds-danger-soft);
    color: var(--ds-danger-hover);
  }

  .logout-button svg {
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }

  .environment-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    color: var(--ds-text-muted);
    background: var(--ds-surface-2);
    border-radius: var(--ds-radius-sm);
    margin: 0 1rem 1.5rem;
  }

  .environment-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--ds-accent);
    flex-shrink: 0;
  }

  .environment-text {
    font-weight: 500;
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-menu-button {
    display: none;
  }

  @media (max-width: 768px) {
    .navbar-container {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }

    .navbar-container.open {
      transform: translateX(0);
    }

    .mobile-menu-button {
      display: block;
      position: fixed;
      top: 1rem;
      left: 1rem;
      z-index: 1001;
      background: var(--ds-surface);
      border: none;
      padding: 0.75rem;
      cursor: pointer;
      color: var(--ds-text-muted);
      border-radius: var(--ds-radius-sm);
      box-shadow: var(--ds-shadow-card);
    }

    .mobile-menu-button:hover {
      color: var(--ds-text);
      background: var(--ds-surface-2);
    }

    .mobile-menu-button svg {
      width: 24px;
      height: 24px;
      stroke-width: 2;
    }

    .mobile-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 999;
    }

    .mobile-overlay.open {
      display: block;
    }
  }
</style>

<button
  class="mobile-menu-button"
  on:click|stopPropagation={() => isMobileMenuOpen = !isMobileMenuOpen}
  aria-label="Toggle menu">
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    {#if isMobileMenuOpen}
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    {:else}
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    {/if}
  </svg>
</button>

<div class="mobile-overlay" class:open={isMobileMenuOpen} on:click={() => isMobileMenuOpen = false} />

<div class="navbar-container" class:open={isMobileMenuOpen}>
  <div class="navbar-content">
    <div class="logo-container">
      <img
        src="/images/vollume-logo-black.png"
        class="logo"
        alt="Vollume Logo" />
    </div>

    <div class="environment-status">
      <span class="environment-dot"></span>
      <span class="environment-text" title={process.env.API_ROOT}>
        {process.env.API_ROOT}
      </span>
    </div>

    <ul class="nav-links">
      {#if $canAccess.analytics}
      <li class="nav-item">
        <span
          class="nav-link"
          class:active={currentSection === 'dashboard'}
          on:click={() => set_section('dashboard')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          Dashboard
        </span>
      </li>
      {/if}
      {#if $canAccess.user_list}
      <li class="nav-item">
        <span
          class="nav-link"
          class:active={currentSection === 'user_list_v2'}
          on:click={() => set_section('user_list_v2')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="8.5" cy="7" r="4"></circle>
            <path d="M20 8v6M23 11h-6"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          User List
        </span>
      </li>
      {/if}
      {#if $canAccess.workspace_info}
      <li class="nav-item">
        <span
          class="nav-link"
          class:active={currentSection === 'workspaces'}
          on:click={() => set_section('workspaces')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          Workspaces
        </span>
      </li>
      {/if}
      {#if $canAccess.admin_management}
      <li class="nav-item">
        <span
          class="nav-link"
          class:active={currentSection === 'admin_users'}
          on:click={() => set_section('admin_users')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
          Admin Users
        </span>
      </li>
      {/if}
      {#if $canAccess.billing_plans}
      <li class="nav-item">
        <span
          class="nav-link"
          class:active={currentSection === 'billing_plans'}
          on:click={() => set_section('billing_plans')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2"></rect>
            <path d="M7 8h10"></path>
            <path d="M7 12h10"></path>
            <path d="M7 16h6"></path>
          </svg>
          Billing Plans
        </span>
      </li>
      {/if}
    </ul>

    <div class="logout-section">
      <button
        class="logout-button"
        on:click={logout}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
          <polyline points="16 17 21 12 16 7"></polyline>
          <line x1="21" y1="12" x2="9" y2="12"></line>
        </svg>
        Logout
      </button>
    </div>
  </div>
</div>
